import { useCallback, useEffect, useRef, useState } from 'react'
import { apiFetch } from '../lib/api.js'

/**
 * Gère le cycle question/réponse/feedback partagé entre GamePage (multi) et SoloGamePage.
 *
 * GamePage utilise en plus les refs exposées pour intégrer son polling de positions
 * (attaques, gel, pause externe). SoloGamePage n'en a pas besoin.
 *
 * @param {object} opts
 * @param {string}   opts.code            - Code de la partie
 * @param {object}   opts.session         - Session token du joueur
 * @param {function} opts.onInitError     - Appelé si GET /me échoue (ex: navigate('/join'))
 * @param {function} opts.onNotPlaying    - Appelé si la partie n'est pas en cours (ex: navigate('/lobby'))
 * @param {object}   opts.extraPauseRef   - Ref booléenne supplémentaire pour bloquer le timer (ex: tooltipActiveRef)
 */
export function useQuestionFlow({ code, session, onInitError, onNotPlaying, extraPauseRef }) {
  const [kart, setKart]                   = useState(null)
  const [game, setGame]                   = useState(null)
  const [question, setQuestion]           = useState(null)
  const [phase, setPhase]                 = useState('loading')
  const [selectedIdx, setSelectedIdx]     = useState(null)
  const [feedbackData, setFeedbackData]   = useState(null)
  const [timerMin, setTimerMin]           = useState(5)
  const [timerMax, setTimerMax]           = useState(30)
  const [timeLeft, setTimeLeft]           = useState(30)
  const [error, setError]                 = useState('')
  const [gamePaused, setGamePaused]       = useState(false)
  const [bonusAnim, setBonusAnim]         = useState(null)
  const [bonusSlotMachine, setBonusSlotMachine] = useState(false)
  const [answerHidden, setAnswerHidden]   = useState(false)
  const [coquilleActive, setCoquilleActive] = useState(false)

  // Refs internes exposées pour que GamePage puisse intégrer son polling
  const submittingRef          = useRef(false)
  const pausedRef              = useRef(false)
  const frozenRef              = useRef(false)
  const phaseRef               = useRef('loading')
  const timerRef               = useRef(null)
  const selectionTimerRef      = useRef(null)
  const autoAdvanceTimerRef    = useRef(null)
  const apiDoneRef             = useRef(false)
  const autoAdvanceDoneRef     = useRef(false)
  const apiKartRef             = useRef(null)
  const bonusJustUsedRef       = useRef(false)
  const fetchNextQuestionRef   = useRef(null)
  const waitingForUnpauseRef   = useRef(false)
  const gameRef                = useRef(null)
  const timerMinRef            = useRef(5)
  const prefetchedRef          = useRef(null)  // résultat pré-chargé { question, done, coquille_active }
  const prefetchingRef         = useRef(null)  // Promise en cours de pré-chargement

  useEffect(() => { phaseRef.current  = phase  }, [phase])
  useEffect(() => { gameRef.current   = game   }, [game])
  useEffect(() => { timerMinRef.current = timerMin }, [timerMin])

  // Chargement initial
  useEffect(() => {
    if (!session) { onInitError?.(); return }
    apiFetch(`/api/karts/${code}/me`, { headers: { 'x-session-token': session.token } })
      .then(({ kart: k, game: g }) => {
        if (k.statut === 'finished' || g.statut === 'finished') {
          setKart(k); setGame(g); setPhase('done'); return
        }
        if (g.statut !== 'playing' && g.statut !== 'paused') {
          onNotPlaying?.(); return
        }
        setKart(k)
        setGame(g)
        setTimerMin(g.config?.timer_min ?? 5)
        setTimerMax(g.config?.timer_max ?? 30)
        if (g.statut === 'paused') {
          pausedRef.current = true
          setGamePaused(true)
          waitingForUnpauseRef.current = true
          // Ne pas appeler fetchNextQuestion : le backend refuse les parties en pause.
          // Le polling (GamePage) ou le bouton Reprendre (SoloGamePage) déclenchera la reprise.
        } else {
          fetchNextQuestionRef.current?.(k, g)
        }
      })
      .catch(() => onInitError?.())
  }, [code])

  function tryAdvanceToNextQuestion() {
    if (!apiDoneRef.current || !autoAdvanceDoneRef.current) return
    fetchNextQuestionRef.current?.(apiKartRef.current, gameRef.current)
  }

  const prefetchNextQuestion = useCallback(() => {
    if (prefetchingRef.current || prefetchedRef.current) return
    const promise = apiFetch(
      `/api/karts/${code}/next-question`,
      { headers: { 'x-session-token': session.token } }
    ).then(data => {
      prefetchedRef.current   = data
      prefetchingRef.current  = null
    }).catch(() => {
      prefetchingRef.current = null
    })
    prefetchingRef.current = promise
  }, [code, session])

  const fetchNextQuestion = useCallback(async (_currentKart, currentGame) => {
    if (phaseRef.current === 'done') return
    setPhase('loading')
    setSelectedIdx(null)
    setAnswerHidden(false)
    setFeedbackData(null)
    submittingRef.current      = false
    apiDoneRef.current         = false
    autoAdvanceDoneRef.current = false
    apiKartRef.current         = null
    clearTimeout(selectionTimerRef.current)
    clearTimeout(autoAdvanceTimerRef.current)
    try {
      let fetchData = prefetchedRef.current
      if (fetchData) {
        prefetchedRef.current  = null
        prefetchingRef.current = null
      } else {
        if (prefetchingRef.current) {
          await prefetchingRef.current
          fetchData = prefetchedRef.current
          if (fetchData) {
            prefetchedRef.current  = null
            prefetchingRef.current = null
          }
        }
        if (!fetchData) {
          fetchData = await apiFetch(
            `/api/karts/${code}/next-question`,
            { headers: { 'x-session-token': session.token } }
          )
        }
      }
      const { question: q, done, coquille_active } = fetchData
      if (done) { setPhase('done'); return }
      setCoquilleActive(!!coquille_active)
      setQuestion(q)

      const tMax = currentGame?.config?.timer_max ?? timerMax
      const effectiveTMax = coquille_active ? Math.max(Math.round(tMax * 0.5), 3) : tMax

      setTimeLeft(effectiveTMax)
      setPhase('question')

      clearInterval(timerRef.current)
      timerRef.current = setInterval(() => {
        if (pausedRef.current || frozenRef.current || extraPauseRef?.current) return
        setTimeLeft(t => {
          if (t <= 1) {
            clearInterval(timerRef.current)
            handleSubmit(null, q, effectiveTMax)
            return 0
          }
          return t - 1
        })
      }, 1000)
    } catch (e) {
      if (e.message?.includes('pas en cours')) {
        phaseRef.current = 'done'
        setPhase('done')
      } else {
        setError(e.message)
      }
    }
  }, [code, session, timerMax])

  fetchNextQuestionRef.current = fetchNextQuestion

  async function handleSubmit(answerIdx, currentQuestion, currentTMax) {
    if (submittingRef.current) return
    submittingRef.current = true
    clearInterval(timerRef.current)
    clearTimeout(selectionTimerRef.current)
    clearTimeout(autoAdvanceTimerRef.current)

    const q          = currentQuestion ?? question
    const tMax       = currentTMax ?? timerMax
    const isTimeout  = answerIdx === null
    const correcte   = !isTimeout && answerIdx === q.bonne_reponse
    const bonneReponseTexte = q.reponses[q.bonne_reponse]?.texte ?? q.reponses[q.bonne_reponse]

    apiDoneRef.current         = false
    autoAdvanceDoneRef.current = false
    apiKartRef.current         = null

    setSelectedIdx(answerIdx)
    setAnswerHidden(false)

    const visualDelay = isTimeout ? 0 : 500
    selectionTimerRef.current = setTimeout(() => {
      if (phaseRef.current === 'done') return
      setFeedbackData({ correcte, isTimeout, bonneReponseTexte })
      setAnswerHidden(true)
      setPhase('feedback')

      const advanceDelay = correcte ? 2 : (gameRef.current?.config?.timer_min ?? timerMinRef.current)
      autoAdvanceTimerRef.current = setTimeout(() => {
        autoAdvanceDoneRef.current = true
        tryAdvanceToNextQuestion()
      }, advanceDelay * 1000)
    }, visualDelay)

    try {
      const result = await apiFetch(`/api/karts/${code}/answer`, {
        method:  'POST',
        headers: { 'x-session-token': session.token },
        body: JSON.stringify({
          question_id:    q.id,
          reponse_donnee: answerIdx,
          temps_reponse:  answerIdx !== null ? Math.round((tMax - timeLeft) * 1000) : null,
        }),
      })
      if (result.finished) {
        clearTimeout(selectionTimerRef.current)
        clearTimeout(autoAdvanceTimerRef.current)
        setKart(result.kart)
        setPhase('done')
        return
      }
      const updatedKart = result.kart
      setKart(updatedKart)
      apiKartRef.current = updatedKart
      if (result.bonus_collected) {
        setBonusSlotMachine(true)
        setTimeout(() => setBonusSlotMachine(false), 1000)
      }
      apiDoneRef.current = true
      prefetchNextQuestion()
      tryAdvanceToNextQuestion()
    } catch (e) {
      setError(e.message)
      submittingRef.current = false
      clearTimeout(selectionTimerRef.current)
      clearTimeout(autoAdvanceTimerRef.current)
      setSelectedIdx(null)
      setAnswerHidden(false)
      setPhase('question')
    }
  }

  // Nettoyage
  useEffect(() => () => {
    clearInterval(timerRef.current)
    clearTimeout(selectionTimerRef.current)
    clearTimeout(autoAdvanceTimerRef.current)
    prefetchedRef.current  = null
    prefetchingRef.current = null
  }, [])

  return {
    // État
    kart, game, question, phase,
    selectedIdx, feedbackData,
    timerMin, timerMax, timeLeft,
    error, gamePaused,
    bonusAnim, setBonusAnim,
    bonusSlotMachine, answerHidden, coquilleActive,
    // Setters exposés pour le polling GamePage et handleUseBonus
    setKart, setGame, setPhase, setGamePaused, setError,
    // Actions
    handleSubmit,
    // Refs exposées pour le polling GamePage
    phaseRef, pausedRef, frozenRef,
    bonusJustUsedRef, fetchNextQuestionRef, waitingForUnpauseRef,
    timerRef, selectionTimerRef, autoAdvanceTimerRef, submittingRef,
  }
}
